import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ChevronLeft, ChevronRight, X, Trophy, Swords, Zap, Disc, MapPin, CalendarDays, Timer } from "lucide-react";
import PublicRacerModal from "@/components/PublicRacerModal";

export default function Schedule() {
  const [nights, setNights] = useState([]);
  const [syndicates, setSyndicates] = useState({});
  const [racersMap, setRacersMap] = useState({});
  const [activeRacerId, setActiveRacerId] = useState(null);

  // Calendar states
  const [currentDate, setCurrentDate] = useState(new Date(2026, 8, 1)); // Default to Sept 2026 (Season 1 starts)
  const [selectedNight, setSelectedNight] = useState(null); // Clicked Race Night document
  const [selectedNightDetail, setSelectedNightDetail] = useState(null); // Full detail including races/results
  const [selectedRace, setSelectedRace] = useState(null); // Secondary overlay for specific race details

  useEffect(() => {
    api.get("/race-nights").then((r) => {
      setNights(r.data);
      // Auto-set current date to first scheduled race night's month if available
      if (r.data.length > 0) {
        const firstNight = new Date(r.data[0].scheduled_at);
        setCurrentDate(new Date(firstNight.getFullYear(), firstNight.getMonth(), 1));
      }
    });
    api.get("/syndicates").then((r) => {
      const m = {}; r.data.forEach((s) => (m[s.id] = s)); setSyndicates(m);
    });
    api.get("/racers").then((r) => {
      const m = {}; r.data.forEach((rc) => (m[rc.id] = rc)); setRacersMap(m);
    });
  }, []);

  useEffect(() => {
    if (selectedNight || selectedRace) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [selectedNight, selectedRace]);

  // Fetch full night details when a night is clicked
  const handleSelectNight = async (night) => {
    setSelectedNight(night);
    setSelectedRace(null);
    try {
      const { data } = await api.get(`/race-nights/${night.id}/full`);
      setSelectedNightDetail(data);
    } catch (err) {
      console.error("Failed to load race night details:", err);
    }
  };

  // Month navigation helpers
  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  // Generate Calendar Grid Days
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  
  const firstDayIndex = new Date(year, month, 1).getDay(); // Day of the week for 1st of month (0=Sun, 6=Sat)
  const totalDays = new Date(year, month + 1, 0).getDate(); // Total days in month
  
  const daysArray = Array.from({ length: totalDays }, (_, i) => i + 1);
  const emptyBoxes = Array.from({ length: firstDayIndex });

  // Map scheduled race nights by local date string
  const getNightsOnDate = (dayNum) => {
    return nights.filter((n) => {
      const nDate = new Date(n.scheduled_at);
      return (
        nDate.getFullYear() === year &&
        nDate.getMonth() === month &&
        nDate.getDate() === dayNum
      );
    });
  };

  const getRoundScores = (round) => {
    let scoreA = 0;
    let scoreB = 0;
    let hasResults = false;
    
    (round.races || []).forEach(race => {
      (race.results || []).forEach(res => {
        hasResults = true;
        const racerObj = racersMap[res.racer_id];
        if (racerObj) {
          if (racerObj.syndicate_id === round.syndicate_a_id) {
            scoreA += res.points || 0;
          } else if (racerObj.syndicate_id === round.syndicate_b_id) {
            scoreB += res.points || 0;
          }
        }
      });
    });
    
    return { scoreA, scoreB, hasResults };
  };

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 space-y-10">
      <div>
        <div className="small-label text-rtrl-red flex items-center gap-1.5">
          <CalendarDays className="w-3.5 h-3.5" /> Galactic Schedule Grid
        </div>
        <h1 className="font-display uppercase text-5xl md:text-6xl text-white mt-2">Championship Calendar</h1>
        <p className="text-white/60 mt-2 max-w-2xl">
          Matchups follow a double-header format. Times auto-adjust and display in your local timezone. Click on calendar races or locked tags to view rosters and completion speeds.
        </p>
      </div>

      {/* Calendar Controller */}
      <div className="rtrl-card carbon-bg p-6">
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-surface-line/40">
          <button onClick={prevMonth} className="rtrl-btn-ghost text-xs py-1.5 px-3 flex items-center gap-1">
            <ChevronLeft className="w-4 h-4" /> Prev
          </button>
          <h2 className="font-display text-4xl uppercase tracking-wider text-white">
            {monthNames[month]} {year}
          </h2>
          <button onClick={nextMonth} className="rtrl-btn-ghost text-xs py-1.5 px-3 flex items-center gap-1">
            Next <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Days of Week Header */}
        <div className="grid grid-cols-7 gap-2 text-center text-xs font-mono-tech uppercase text-rtrl-silver font-bold pb-2">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(d => (
            <div key={d} className="py-2">{d}</div>
          ))}
        </div>

        {/* Calendar Grid Boxes */}
        <div className="grid grid-cols-7 gap-2 auto-rows-[120px] md:auto-rows-[140px]">
          {emptyBoxes.map((_, idx) => (
            <div key={`empty-${idx}`} className="bg-void/10 border border-surface-line/10 opacity-30 hud-chamfer-sm" />
          ))}

          {daysArray.map((dayNum) => {
            const matches = getNightsOnDate(dayNum);
            const hasMatch = matches.length > 0;
            return (
              <div
                key={dayNum}
                className={`p-2 border hud-chamfer-sm bg-surface transition-all flex flex-col justify-between ${
                  hasMatch
                    ? "border-rtrl-red/40 bg-rtrl-red/5 hover:border-rtrl-red hover:shadow-[0_0_15px_rgba(229,25,25,0.1)] cursor-pointer"
                    : "border-surface-line hover:border-surface-hi"
                }`}
                onClick={() => hasMatch && handleSelectNight(matches[0])}
                data-testid={`calendar-day-${dayNum}`}
              >
                <span className={`font-mono-tech text-xs font-bold ${hasMatch ? "text-rtrl-red" : "text-white/40"}`}>
                  {dayNum}
                </span>
                
                {hasMatch && (
                  <div className="space-y-1 text-left shrink-0 w-full overflow-hidden">
                    {matches.map((n) => {
                      const localTime = new Date(n.scheduled_at).toLocaleTimeString(undefined, {
                        hour: "2-digit",
                        minute: "2-digit"
                      });
                      
                      // Check if there are completed results to render inline
                      const completedRounds = (n.rounds || []).filter(r => {
                        const { hasResults } = getRoundScores(r);
                        return hasResults;
                      });
                      
                      const hasAnyResults = completedRounds.length > 0;

                      return (
                        <div key={n.id} className="text-[10px] leading-tight">
                          <div className="text-white font-bold uppercase truncate font-display tracking-wide">{n.label}</div>
                          {!hasAnyResults ? (
                            <div className="text-rtrl-red font-mono-tech font-semibold text-[9px]">{localTime}</div>
                          ) : (
                            <div className="space-y-0.5 mt-0.5" data-testid={`calendar-scores-${n.id}`}>
                              {n.rounds.map((r, rIdx) => {
                                const { scoreA, scoreB, hasResults } = getRoundScores(r);
                                if (!hasResults) return null;
                                const tagA = syndicates[r.syndicate_a_id]?.tag || "TBD";
                                const tagB = syndicates[r.syndicate_b_id]?.tag || "TBD";
                                return (
                                  <div key={rIdx} className="text-[8px] bg-void/50 px-1 py-0.5 rounded font-mono-tech flex justify-between border border-surface-line/20">
                                    <span className={scoreA >= scoreB ? "text-emerald-400 font-bold" : "text-white/60"}>{tagA}:{scoreA}</span>
                                    <span className="text-white/30">v</span>
                                    <span className={scoreB >= scoreA ? "text-emerald-400 font-bold" : "text-white/60"}>{tagB}:{scoreB}</span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Primary Race Night Detail Modal Overlay */}
      {selectedNight && (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-4 bg-void/80 backdrop-blur-sm animate-fade-in">
          <div className="relative w-full max-w-4xl max-h-[85vh] overflow-y-auto rtrl-card carbon-bg p-6 md:p-8 border border-surface-line shadow-[0_0_50px_rgba(229,25,25,0.2)]">
            <button
              onClick={() => { setSelectedNight(null); setSelectedNightDetail(null); }}
              className="absolute top-4 right-4 text-white/40 hover:text-rtrl-red p-1.5 transition-colors"
              data-testid="night-modal-close"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="space-y-6">
              <div>
                <div className="small-label text-rtrl-red mb-1">Double-Header Uplink</div>
                <h2 className="font-display uppercase text-4xl text-white">{selectedNight.label}</h2>
                <div className="small-label font-mono-tech mt-1 text-rtrl-silver">
                  🗓️ {new Date(selectedNight.scheduled_at).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric", year: "numeric" })}
                  <span className="mx-2 text-rtrl-red">·</span>
                  ⏰ Local Time: {new Date(selectedNight.scheduled_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
                </div>
              </div>

              {selectedNightDetail ? (
                <div className="space-y-6">
                  {selectedNightDetail.rounds.map((r, rIdx) => {
                    const isPod = r.discipline === "pod";
                    return (
                      <div key={r.id} className="border border-surface-line bg-void/40 p-4 rounded-lg flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-display text-2xl text-white">Round {r.round_number}</span>
                            {isPod ? <span className="disc-chip-pod">Pod Racing</span> : <span className="disc-chip-speeder">Speeder Racing</span>}
                          </div>
                          
                          {/* Matchup Header */}
                          <div className="text-white font-body flex items-center gap-2 mt-2 flex-wrap text-sm md:text-base">
                            {r.syndicate_a_id ? (
                              <Link to={`/syndicates/${r.syndicate_a_id}`} className="flex items-center gap-1.5 text-white hover:text-rtrl-red transition-colors font-bold">
                                {syndicates[r.syndicate_a_id]?.logo_url ? (
                                  <img src={`${process.env.REACT_APP_BACKEND_URL}${syndicates[r.syndicate_a_id].logo_url}`} alt="" className="w-5 h-5 rounded-full object-contain shrink-0" />
                                ) : (
                                  <div className="w-2 h-2 rounded-full shrink-0" style={{ background: syndicates[r.syndicate_a_id]?.color }} />
                                )}
                                <span>{syndicates[r.syndicate_a_id]?.name}</span>
                              </Link>
                            ) : <span className="text-white/40">TBD</span>}
                            <span className="text-rtrl-red font-display text-lg">VS</span>
                            {r.syndicate_b_id ? (
                              <Link to={`/syndicates/${r.syndicate_b_id}`} className="flex items-center gap-1.5 text-white hover:text-rtrl-red transition-colors font-bold">
                                {syndicates[r.syndicate_b_id]?.logo_url ? (
                                  <img src={`${process.env.REACT_APP_BACKEND_URL}${syndicates[r.syndicate_b_id].logo_url}`} alt="" className="w-5 h-5 rounded-full object-contain shrink-0" />
                                ) : (
                                  <div className="w-2 h-2 rounded-full shrink-0" style={{ background: syndicates[r.syndicate_b_id]?.color }} />
                                )}
                                <span>{syndicates[r.syndicate_b_id]?.name}</span>
                              </Link>
                            ) : <span className="text-white/40">TBD</span>}
                          </div>
                        </div>

                        {/* Interactive Clicking on Races */}
                        <div className="grid grid-cols-4 gap-2 flex-1 md:max-w-md">
                          {r.races.map((race) => (
                            <button
                              key={race.id}
                              onClick={() => setSelectedRace({ ...race, round: r })}
                              className="border border-surface-line/50 p-2 hud-chamfer-sm bg-void/50 hover:border-rtrl-red transition-all text-center flex flex-col justify-between h-16 w-full"
                              data-testid={`round-race-btn-${race.id}`}
                            >
                              <div className="text-[10px] text-white/40 uppercase font-mono-tech w-full">Race {race.race_number}</div>
                              <div className="text-white text-xs font-bold truncate w-full">{race.track_name || "TBD"}</div>
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="py-12 text-center text-white/50">Accessing round detail logs…</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Secondary Race Detail Overlay (Track, Lineups, and Placement Speed results) */}
      {selectedRace && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-void/90 backdrop-blur-sm animate-fade-in">
          <div className="relative w-full max-w-lg rtrl-card carbon-bg p-6 md:p-8 border border-surface-line shadow-[0_0_50px_rgba(229,25,25,0.3)]">
            <button
              onClick={() => setSelectedRace(null)}
              className="absolute top-4 right-4 text-white/40 hover:text-rtrl-red p-1.5 transition-colors"
              data-testid="race-modal-close"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="space-y-6">
              <div>
                <div className="small-label text-rtrl-red mb-1">Race Placement Uplink</div>
                <h2 className="font-display uppercase text-4xl text-white">Race {selectedRace.race_number} Log</h2>
                <div className="small-label font-mono-tech text-rtrl-silver mt-1 flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5 text-rtrl-red shrink-0" />
                  Track Course: <span className="text-white font-bold">{selectedRace.track_name || "Track TBD"}</span>
                </div>
              </div>

              {/* Lineups */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-void/50 border border-surface-line/40 rounded-lg">
                  <div className="small-label text-pod mb-2">{syndicates[selectedRace.round.syndicate_a_id]?.tag || "Syndicate A"} Lineup</div>
                  <div className="space-y-1 max-h-36 overflow-y-auto">
                    {(selectedRace.locked_racer_ids || [])
                      .map(id => racersMap[id])
                      .filter(rc => rc && rc.syndicate_id === selectedRace.round.syndicate_a_id)
                      .map(rc => (
                        <button
                          key={rc.id}
                          onClick={() => { setActiveRacerId(rc.id); }}
                          className="text-xs text-white/80 hover:text-rtrl-red block truncate font-mono-tech w-full text-left"
                        >
                          #{rc.number ?? "-"} {rc.display_name}
                        </button>
                      ))}
                    {!(selectedRace.locked_racer_ids || []).some(id => racersMap[id]?.syndicate_id === selectedRace.round.syndicate_a_id) && (
                      <span className="text-white/20 text-xs italic">Not announced</span>
                    )}
                  </div>
                </div>

                <div className="p-3 bg-void/50 border border-surface-line/40 rounded-lg">
                  <div className="small-label text-speeder mb-2">{syndicates[selectedRace.round.syndicate_b_id]?.tag || "Syndicate B"} Lineup</div>
                  <div className="space-y-1 max-h-36 overflow-y-auto">
                    {(selectedRace.locked_racer_ids || [])
                      .map(id => racersMap[id])
                      .filter(rc => rc && rc.syndicate_id === selectedRace.round.syndicate_b_id)
                      .map(rc => (
                        <button
                          key={rc.id}
                          onClick={() => { setActiveRacerId(rc.id); }}
                          className="text-xs text-white/80 hover:text-rtrl-red block truncate font-mono-tech w-full text-left"
                        >
                          #{rc.number ?? "-"} {rc.display_name}
                        </button>
                      ))}
                    {!(selectedRace.locked_racer_ids || []).some(id => racersMap[id]?.syndicate_id === selectedRace.round.syndicate_b_id) && (
                      <span className="text-white/20 text-xs italic">Not announced</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Placement Speed Results */}
              <div className="space-y-2">
                <div className="small-label text-rtrl-silver border-b border-surface-line pb-1.5 flex items-center gap-1.5">
                  <Trophy className="w-3.5 h-3.5 text-rtrl-red" /> Official Finish Times &amp; placements
                </div>
                
                <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                  {(selectedRace.results || []).length > 0 ? (
                    [...selectedRace.results]
                      .sort((a, b) => a.position - b.position)
                      .map((res) => {
                        const rc = racersMap[res.racer_id];
                        return (
                          <div key={res.id} className="flex justify-between items-center bg-void/35 border border-surface-line/20 px-3 py-2 rounded text-xs gap-3">
                            <button
                              onClick={() => { setActiveRacerId(res.racer_id); }}
                              className="text-white font-medium hover:text-rtrl-red font-body truncate flex-1 text-left"
                            >
                              {rc ? rc.display_name : "Unknown Racer"} {rc?.number !== null && <span className="text-white/30 text-[10px]">#{rc.number}</span>}
                            </button>
                            <div className="flex items-center gap-3 shrink-0 font-mono-tech font-bold">
                              {res.race_time && (
                                <span className="text-white/50 text-[10px] font-normal flex items-center gap-1">
                                  <Timer className="w-3 h-3 text-indigo-400" /> {res.race_time}
                                </span>
                              )}
                              <span className={res.dnf ? "text-rtrl-red" : res.position === 1 ? "text-amber-400" : "text-white"}>
                                {res.dnf ? "DNF" : `P${res.position}`}
                              </span>
                            </div>
                          </div>
                        );
                      })
                  ) : (
                    <div className="text-center py-6 text-white/30 italic text-xs">Waiting for race night broadcast. Grid scores not posted yet.</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {nights.length === 0 && (
        <div className="text-white/40 italic p-12 text-center rtrl-card carbon-bg max-w-lg mx-auto">No custom race calendar generated yet. Standby for HoloNet upload.</div>
      )}

      <PublicRacerModal racerId={activeRacerId} onClose={() => setActiveRacerId(null)} />
    </div>
  );
}